/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Switch } from 'antd'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import { font, weightMedium } from 'src/components/css'

import { GameDatePicker, GameTimePicker } from './pickers'
import { TBD_CELL_VALUE } from '../utils/gameGridRows'
import type { GameGridRow } from '../utils/gameGridRows'
import { gameViewsLabel } from '../utils/i18n'

/**
 * The cell editors are the BULK PANEL'S pickers, in a cell.
 *
 * Not "the same kind of picker" — the same two components, imported from
 * `./pickers`, which in turn wrap the pickers the rest of the app uses: antd's
 * `DatePicker` and the Scheduler Wizard's `TimePickerWithInterval`. Retiming one
 * game from its row and retiming a hundred from the toolbar are the same
 * decision, so they get the same control.
 *
 * These stay INLINE editors — the field lives in the cell and the row does not
 * change shape — and both panels portal to `document.body`, so antd and
 * TimePickerWithInterval position them against the page and no ancestor's
 * `overflow: hidden` clips them.
 *
 * The auto-close is fixed at the other end: both panels suppress mousedown, so
 * a click on a month arrow or a time slot never moves focus out of the cell and
 * `stopEditingWhenCellsLoseFocus` has nothing to react to. Moving the panels
 * into the cell instead was the wrong lever — it makes rc-trigger position the
 * calendar relative to the cell, which then needs `position: fixed` to escape
 * the cell, at which point the two coordinate systems disagree and the calendar
 * lands in the corner of the page.
 *
 * The adapter's whole job is the value boundary and the commit. Cells hold
 * canonical civil strings — `YYYY-MM-DD` and `HH:mm` — and the pickers speak the
 * same, so there is no conversion left to get wrong.
 */

/**
 * Both panels hang off the page, not the cell. See the note above: the cell
 * clips, and escaping the clip costs correct positioning.
 */
const IN_BODY = () => document.body

/** AG Grid unmounts the editor synchronously, so let the picker finish first. */
const closeAfterCommit = (stopEditing: () => void) => {
  queueMicrotask(stopEditing)
}

export const DateCellEditor = ({
  value,
  onValueChange,
  stopEditing,
}: CustomCellEditorProps<GameGridRow>) => {
  const original = typeof value === 'string' ? value : ''

  return (
    <div css={fieldStyles}>
      <GameDatePicker
        autoFocus
        value={original === '' ? null : original}
        getPopupContainer={IN_BODY}
        onChange={(next: string) => {
          onValueChange(next)
          closeAfterCommit(stopEditing)
        }}
      />
    </div>
  )
}

/**
 * Time, plus the switch that takes the time AWAY.
 *
 * The grid could already promote a TBD game to a real time from this cell but
 * never put it back — the Add/Edit Game modal had the only TBD control in the
 * product. This closes that, on the same sentinel the modal writes.
 *
 * The two directions are deliberately asymmetric:
 *
 *   ON  commits `TBD_CELL_VALUE` and closes. There is nothing further to ask.
 *   OFF commits NOTHING. It re-enables the picker and waits. Committing "off"
 *       would have to invent a start time, which is what the modal's
 *       `clearTimeToBeDefined` does — but a form the admin still reviews before
 *       Confirm can afford that, and a grid cell has no such step. Picking a
 *       real time is the clear, and `setLocalTime` already promotes the game
 *       out of the sentinel.
 */
export const TimeCellEditor = ({
  value,
  data,
  onValueChange,
  stopEditing,
}: CustomCellEditorProps<GameGridRow>) => {
  const committed = typeof value === 'string' ? value : ''
  //  The marker is not a time; the picker must never be seeded with it.
  const original = committed === TBD_CELL_VALUE ? '' : committed
  const [isTbd, setIsTbd] = useState(
    data?.isTbd === true || committed === TBD_CELL_VALUE
  )

  return (
    <div css={fieldStyles}>
      <GameTimePicker
        autoFocus
        disabled={isTbd}
        placeholder={isTbd ? gameViewsLabel('gameGridTbd') : undefined}
        value={original === '' ? null : original}
        getPopupContainer={IN_BODY}
        onChange={(next: string) => {
          onValueChange(next)
          closeAfterCommit(stopEditing)
        }}
      />
      {/*
        The mousedown must neither move focus NOR reach `document`.

        Two separate ways this switch gets torn down before its `onChange` can
        run, and it needs both guards:

        `preventDefault` — `TimePickerWithInterval` commits its typed text on
        blur, which for this editor means `onValueChange` + `stopEditing`, so a
        bare click beside the input tears the editor down before the switch
        reacts. Cancelling the focus move stops that. Same suppression the two
        dropdown panels use; the click itself still fires.

        `stopPropagation` — and this is the one that was missing. While its list
        is open the picker also holds a listener on **`document`** for
        click-outside, and the switch is outside both the input and the
        dropdown. `preventDefault` does nothing about that: it cancels the
        default action, not other listeners. So every click on the switch ran
        `commitTypedText()` + `closeDropdown()`, the editor unmounted, and the
        toggle could never be operated at all — the list opens on its own, so
        the listener was always armed. Stopping propagation here keeps the
        native event from ever reaching `document`.
      */}
      <span
        css={toggleStyles}
        title={gameViewsLabel('gameGridEditTimeTbd')}
        onMouseDown={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        <Switch
          size='small'
          checked={isTbd}
          aria-label={gameViewsLabel('gameGridEditTimeTbd')}
          onChange={(next: boolean) => {
            setIsTbd(next)
            if (!next) {
              return
            }
            onValueChange(TBD_CELL_VALUE)
            closeAfterCommit(stopEditing)
          }}
        />
        <span css={toggleLabelStyles}>{gameViewsLabel('gameGridTbd')}</span>
      </span>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/**
 * Fills the cell. The only visible difference between reading and editing
 * should be the field and its caret — the row keeps its height, and the rows
 * either side stay readable.
 */
const fieldStyles = css`
  ${font.body}
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  padding: 0 6px;
`

/**
 * antd's `Switch` directly, not `ts-components/SwitchInput` — that one is a
 * settings ROW (title over subtitle, `flex: 1`) built for forms, and it cannot
 * fit a time cell. This is the same control `gameForm`'s TBD switch uses.
 *
 * `flex-shrink: 0` so the picker gives up width first: the field can shorten,
 * but a half-rendered switch is not a control.
 */
const toggleStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  cursor: pointer;
`

const toggleLabelStyles = css`
  ${font.body}
  ${weightMedium}
  font-size: 11px;
  color: ${colors.NEUTRAL_0};
  user-select: none;
`
